import { useEffect, useRef } from 'react';
import moment from 'moment';

import { localTime, controlAPICall } from '@helpers';
import { getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { getStatsRequestParams } from '@components/machines/components/eventsChart/helpers/period';
import { getMax } from '@components/ncFiles/components/paid/components/barChart/calculation';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { UseManagePaidBarChartBarParams, UseManagePaidBarChartBarReturn } from '@components/ncFiles/components/paid/components/barChart/types';

export const useManagePaidBarChartBar = ({
  item,
  id,
  idKey,
  barValueName,
  lineValueName,
  average,
  averagePaid,
  showAverage,
  barAmount,
  chartWidth,
  selectedDate,
  getData,
  saveChartData,
}: UseManagePaidBarChartBarParams): UseManagePaidBarChartBarReturn => {
  const { timePeriod: selectedTime } = useHandleParams();
  const abortController = useRef();

  const maxValue = getMax({ item, barValueName, lineValueName, average, averagePaid, showAverage });

  const loadData = async () => {
    if (!getData || id == null) {
      return;
    }
    const d = {
      [idKey]: id,
      ...getStatsRequestParams(selectedTime, localTime(item?.date)),
    };

    const result = await controlAPICall(getData, d, abortController);

    if (item?.date) {
      if (result) {
        const storage: { dateTime: string }[] = [];

        const displayPeriod = {
          [TimePeriod.DAY]: 'hour',
          [TimePeriod.WEEK]: 'day',
          [TimePeriod.MONTH]: 'day',
          [TimePeriod.YEAR]: 'month',
          [TimePeriod.EIGHT_WEEKS]: 'isoWeek',
        };

        Array.from({ length: barAmount }, (_, i) => {
          const date = moment.utc(d.date).add(i, displayPeriod[selectedTime]);
          const exist = result?.statisticsPerPeriod?.find((el) => moment.utc(el.dateTime).isSame(date));
          if (exist) {
            storage.push(exist);
          } else {
            storage.push({ dateTime: date.format() });
          }
        });

        saveChartData(item.date, {
          data: {
            statisticsOverall: result.statisticsOverall || {},
            statisticsPerPeriod: storage,
          },
          loaded: true,
          error: false,
        });
      } else {
        saveChartData(item.date, {
          data: {
            statisticsOverall: {},
            statisticsPerPeriod: [],
          },
          loaded: true,
          error: true,
        });
      }
    }
  };

  useEffect(() => {
    if (!item?.data.loaded) {
      loadData();
    }
  }, [item?.data.loaded, item?.date, id]);

  const { legend = [], legendRules = [] } = getBarLegend(selectedTime, selectedDate);

  const legendLength = legend.length || 1;
  const bottomLegendBlockWidth = chartWidth / legendLength;

  const getPosition = (v: number) => (maxValue ? (v ? (v * 100) / maxValue : 0) : 0);

  return {
    selectedTime,
    maxValue,
    legend,
    legendRules,
    bottomLegendBlockWidth,
    getPosition,
  };
};
